import React from 'react';
import { Dialog, DialogContent, Box, Typography, Radio, Button, IconButton, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import DynamicQR from './DynamicQR';
import StaticQR from './StaticQR';

interface PixQRCodeModalProps {
  open: boolean;
  onClose: () => void;
}

const PixQRCode: React.FC<PixQRCodeModalProps> = ({ open, onClose }) => {
  const [type, setType] = React.useState<'static' | 'dynamic'>('dynamic');
  const [amount, setAmount] = React.useState('');
  const [amountError, setAmountError] = React.useState(false);
  const [showDynamicQR, setShowDynamicQR] = React.useState(false);
  const [showStaticQR, setShowStaticQR] = React.useState(false);

  const handleGenerateQRCode = () => {
    if (type === 'dynamic' && !amount.trim()) {
      setAmountError(true);
      return;
    }
    setAmountError(false);
    onClose();
    if (type === 'dynamic') {
      setShowDynamicQR(true);
    } else {
      setShowStaticQR(true);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        <DialogContent>
          <Box position="relative" display="flex" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={1} sx={{ mx: 'auto' }}>
              <QrCode2Icon color="primary" />
              <Typography fontWeight={600} textAlign="center" sx={{ fontSize: '20px' }}>
                Receber PIX QRCode
              </Typography>
            </Box>

            <IconButton onClick={onClose} sx={{ position: 'absolute', right: 0, top: 0 }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Typography color="text.secondary" mb={3} textAlign="center" sx={{ fontSize: '16px' }}>
            Gere um QRCode para receber um pagamento
          </Typography>

          <Typography fontWeight={600} mb={2} sx={{ fontSize: '22px' }}>
            Qual o tipo de QRCode você quer gerar?
          </Typography>

          <Box
            onClick={() => setType('static')}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            border="1px solid"
            borderColor={type === 'static' ? 'primary.main' : 'grey.300'}
            borderRadius={2}
            p={2}
            mb={2}
            sx={{ cursor: 'pointer' }}
          >
            <Box display="flex" gap={2}>
              <Radio checked={type === 'static'} />
              <Box>
                <Typography fontWeight={500}>QRCode estático</Typography>
                <Typography variant="body2" color="text.secondary">
                  Pode ser reutilizado
                </Typography>
              </Box>
            </Box>

            <InfoOutlinedIcon color="action" />
          </Box>

          <Box
            onClick={() => setType('dynamic')}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            border="2px solid"
            borderColor={type === 'dynamic' ? 'primary.main' : 'grey.300'}
            borderRadius={2}
            p={2}
            mb={3}
            sx={{ cursor: 'pointer' }}
          >
            <Box display="flex" gap={2}>
              <Radio checked={type === 'dynamic'} />
              <Box>
                <Typography fontWeight={600}>QRCode Dinâmico</Typography>
                <Typography variant="body2" color="text.secondary">
                  Só pode ser utilizado uma única vez em até 24h
                </Typography>
              </Box>
            </Box>

            <InfoOutlinedIcon color="action" />
          </Box>

          {type === 'dynamic' && (
            <>
              <Typography fontWeight={500} mb={1}>
                Valor a receber
              </Typography>

              <TextField
                fullWidth
                placeholder="R$"
                variant="standard"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                  if (amountError) setAmountError(false);
                }}
                error={amountError}
                helperText={amountError ? 'Informe o valor a receber' : undefined}
                InputProps={{
                  sx: { fontSize: 16 },
                }}
              />
            </>
          )}

          <Box mt={4} display="flex" justifyContent="center">
            <Button
              variant="contained"
              size="large"
              sx={{
                borderRadius: 999,
                px: 6,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '14px',
                width: '20vw',
              }}
              onClick={handleGenerateQRCode}
            >
              Gerar QRCode
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <DynamicQR open={showDynamicQR} onClose={() => setShowDynamicQR(false)} amount={amount} />

      <StaticQR open={showStaticQR} onClose={() => setShowStaticQR(false)} />
    </>
  );
};

export default PixQRCode;
